import { history } from "../App";
import is from "is";
import { makeObservable, observable, action } from "mobx";
import FormElementJSON from "../elements/formBuilder/json/form-elements.json";
import { getTableData } from "./graphTableFunctions";
import { getViewData } from "./graphViewFunctions";
import { getCalculationData } from "./graphCalculationFunctions";
import { getSheetUsedRange } from "./graphWorksheetFunctions";
import { getChartDetailInfo } from "./graphChartFunctions";
import { getRangeInfo } from "./graphRangeFunctions";
import { getCellAreaInfo } from "./graphCellAreaFunctions";
import { checkPublicAccessToken } from "./GraphMain";
import { writeElementValueChanges } from "./graphPersistChangesFunctions";
import {
  getUrlParameterForElementJson,
  getGraphElementId,
  getNormalizedElementId,
  getNormalizedElementTypeId,
} from "../utils/helper";
import {
  getRangeStartOffset,
  getSheetNameFromAddress,
} from "../utils/helperExcel";

export class StoreElements {
  isLoading = false;
  elementCacheList = [];
  applySettings = false;
  sheetParameters = {};
  sidePanelVisible = true;
  sheetParamsUpdated = false;
  appWizardItems = [];
  appWizardLastFloatingObjectName = null;
  editableAreasItems = [];
  spreadTableOptions = {};
  hiddenRows = [];
  hiddenColumns = [];

  constructor(rootStore) {
    this.rootStore = rootStore;
    this.elementCacheList = [];
    makeObservable(this, {
      applySettings: observable,
      setApplySettings: action,
      sheetParameters: observable,
      setSheetParameters: action,
      sidePanelVisible: observable,
      setSidePanelVisible: action,
      sheetParamsUpdated: observable,
      setSheetParamsUpdated: action,
      appWizardItems: observable,
      setAppWizardItems: action,
      setAppWizardLastFloatingObjectName: action,
      editableAreasItems: observable,
      setEditableAreasItems: action,
      spreadTableOptions: observable,
      // setSpreadTableOptions: action,
      hiddenRows: observable,
      hiddenColumns: observable,
      setHiddenRows: action,
      setHiddenColumns: action,
    });
  }

  //=======================
  // #range options panel

  setSheetParamsUpdated(updated = true) {
    this.sheetParamsUpdated = updated;
  }

  setSidePanelVisible(visible = true) {
    this.sidePanelVisible = visible;
  }

  setSheetParameters(parameters) {
    this.sheetParameters = parameters;
  }

  setApplySettings(val = true) {
    this.applySettings = val;
  }

  setAppWizardItems(items) {
    this.appWizardItems = items;
  }

  setAppWizardLastFloatingObjectName(floatingObjectName) {
    this.appWizardLastFloatingObjectName = floatingObjectName;
  }

  setLocalViewStartupInfo(startupInfo, showNow = false) {
    this.rootStore.main.setLocalStoreValue("localViewStartupInfo", startupInfo);
    if (!startupInfo) return;
    if (!showNow) return;

    // open the view
    let route;
    if (startupInfo.type === "sample") route = "/files/";
    if (!route) return;

    setTimeout(() => {
      this.rootStore.openUrl(route, true);
    }, 200);
  }

  getLocalViewStartupInfo() {
    let startupInfo = this.rootStore.main.getLocalStoreValue(
      "localViewStartupInfo",
      null,
    );
    // if (startupInfo) {
    //   this.rootStore.main.setLocalStoreValue("localViewStartupInfo", null);
    // }
    return startupInfo;
  }

  setEditableAreasItems(items) {
    this.editableAreasItems = items;
  }

  setHiddenRows(rows) {
    this.hiddenRows = rows;
  }

  setHiddenColumns(columns) {
    this.hiddenColumns = columns;
  }

  //========================
  // #region ELEMENTS

  getElementInfoFromCacheList(cacheId) {
    if (!cacheId) return;

    let foundInfo = null;
    // look for an existing session
    foundInfo = this.elementCacheList.find((obj) => {
      return obj.cacheId === cacheId;
    });
    if (foundInfo) {
      const roughObjSize = JSON.stringify(foundInfo).length;
      console.log("cache found, size", roughObjSize);
    }
    return foundInfo?.elementInfo;
  }

  clearElementCacheList() {
    this.elementCacheList = [];
  }

  storeElementInfoInCacheList(cacheId, elementInfo) {
    if (!cacheId || !elementInfo) return;

    this.elementCacheList.push({
      cacheId: cacheId,
      elementInfo: elementInfo,
    });
  }

  async getElementDetail({
    accessInfo,
    elementId,
    elementType,
    worksheetId,
    sharingInfo,
  }) {
    // look if in cache
    let type = elementType ?? sharingInfo?.type;
    let cacheId = getNormalizedElementTypeId(elementId, type);
    let elementInfo = this.getElementInfoFromCacheList(cacheId);

    let needLiveData = ["range", "sheet"].includes(type);
    if (elementInfo && !needLiveData) {
      console.log("INFO got element details from local cache");
      return elementInfo;
    }
    // are there any caches: cacheInfo = complete configuration object, cacheData = there is a data file on aws
    let cacheInfo = sharingInfo?.cacheInfo ?? {};
    let cacheData = (type === "calculation" && sharingInfo?.cacheData) ?? ""; // only for calculations
    let cacheVersion =
      (type === "calculation" && sharingInfo?.cacheVersion) ?? ""; // only for calculations
    const cachedDataUrl =
      sharingInfo?.cacheInfo?.calculationInfo?.cacheDataUrl ?? "";
    const hasCachedInfo =
      Object.keys(cacheInfo).length > 1 ||
      cacheData.length > 20 ||
      cachedDataUrl.length > 20;
    try {
      let elementName = "";
      // inform the store
      if (accessInfo && !this.rootStore.workbooks.activeDriveItemId) {
        this.rootStore.workbooks.setActiveFile(accessInfo, "element");
      }

      // check if authentication is needed
      if (accessInfo?.connectorId || hasCachedInfo) {
        if (hasCachedInfo) {
          console.log(
            "INFO get info & data from invitation cache - no session needed",
          );
        } else {
          console.time("PROFILE checkPublicAccessToken");

          console.log(
            "INFO get info from invitation cache - get session async",
          );
          await checkPublicAccessToken(accessInfo, sharingInfo);
          console.timeEnd("PROFILE checkPublicAccessToken");
        }
      }

      console.time("PROFILE getData");
      if (type === "table") {
        // get the table data
        elementInfo = await getTableData(accessInfo, elementId);
      }

      if (type === "view") {
        // get the table data
        elementInfo = await getViewData(accessInfo, elementId);
      }

      if (type === "calculation") {
        if (hasCachedInfo) {
          // there is a full cached configuration
          if (elementInfo && elementInfo.accessInfo) {
            elementInfo = sharingInfo.cacheInfo;
            // check if there is a connectorId in the accessInfo
            if (!elementInfo.accessInfo.hasOwnProperty("connectorId")) {
              elementInfo.accessInfo.connectorId = accessInfo.connectorId;
            }
          }
          // we need to download the cache data and read the configuration
          if (!elementInfo && cacheData) {
            console.log("INFO getElementInfoFromCacheData");
            elementInfo =
              await this.rootStore.cache.getElementInfoFromCacheData(
                cacheData,
                cacheVersion,
                cacheId,
              );
            if (elementInfo) {
              elementInfo.calculationId = cacheId;
              elementInfo.accessInfo = accessInfo;
            }
          }
        }
        // get the calculation data
        if (!elementInfo && accessInfo) {
          console.log("INFO no elementInfo - get session based data");

          // create a session entry for this accessToken & driveItemInfo
          await checkPublicAccessToken(accessInfo, sharingInfo);
          // get the elementInfo
          elementInfo = await getCalculationData(accessInfo, elementId);
        }
      }

      if (type === "chart") {
        // get the chart data
        elementInfo = await getChartDetailInfo(
          accessInfo,
          elementId,
          worksheetId,
          2000,
        );
        elementName = elementInfo?.chartTitle;
      }

      if (type === "range") {
        // get the chart data
        elementInfo = await getRangeInfo(accessInfo, elementId);
        elementName = elementInfo?.nameInfo?.name;
      }

      if (type === "cellarea") {
        // get the chart data
        elementInfo = await getCellAreaInfo(accessInfo, elementId);
        elementName = elementInfo?.addressId;
      }

      if (type === "sheet") {
        // get the chart data
        elementInfo = await getSheetUsedRange(accessInfo, elementId);
      }
      console.timeEnd("PROFILE getData");

      //========================
      // enrich elementInfo
      //========================
      let tempWorksheetId = worksheetId
        ? worksheetId
        : elementInfo?.range?.worksheetId;
      let normalizedElementTypeId = getNormalizedElementTypeId(elementId, type);
      let normalizedElementId = getNormalizedElementId(elementId);
      let normalizedWorksheetId = getNormalizedElementId(tempWorksheetId);
      let normalizedWorksheetTypeId = getNormalizedElementTypeId(
        tempWorksheetId,
        "sheet",
      );
      let tempWorksheetName = "";

      // elements with ranges
      if (elementInfo?.range) {
        let rangeAddress = elementInfo.range.address;
        tempWorksheetName = getSheetNameFromAddress(rangeAddress);
        elementInfo.range.rowCount = elementInfo.range.values?.length;
        elementInfo.range.rangeOffset = getRangeStartOffset(rangeAddress);
        elementInfo.range.rangeMaxCount = this.getRangeMaxElements(
          elementInfo.range.cellCount,
          elementInfo.range.values?.length,
        );

        if (type === "sheet") {
          elementName = tempWorksheetName;
        }
        if (type === "table") {
          elementName = elementInfo.elementName;
        }
      }

      if (elementInfo?.calculationInfo?.elementList) {
        let elementList = elementInfo?.calculationInfo?.elementList;
        elementList.forEach((element) => {
          const config = FormElementJSON[element.element]?.description;
          if (config) {
            if (config["label"]) {
              delete element.content;
            }
            if (config["content"]) {
              delete element.label;
            }
          }
        });
      }

      let basics = {
        readOnly: false,
        elementName: elementName,
        elementId: elementId,
        elementNormalizedId: normalizedElementId,
        elementNormalizedTypeId: normalizedElementTypeId,
        elementType: type,
        worksheetId: tempWorksheetId,
        worksheetNormalizedId: normalizedWorksheetId,
        worksheetNormalizedTypeId: normalizedWorksheetTypeId,
        worksheetName: tempWorksheetName,
      };

      let result = elementInfo ? { ...elementInfo, basics } : null;
      this.storeElementInfoInCacheList(cacheId, result);
      return result;
    } catch (err) {
      console.log("ERROR getElementDetail", err);
    }
  }

  getRangeMaxElements(cellCount, rowCount) {
    if (!cellCount || !rowCount) return;

    if (
      this.rootStore.main.limitCellCount < 0 ||
      this.rootStore.main.limitCellCount > cellCount
    )
      return { maxRows: -1, maxColumns: -1 };

    let columnCount = Math.floor(cellCount / rowCount);
    let maxRowCount = Math.floor(
      this.rootStore.main.limitCellCount / columnCount,
    );

    if (columnCount < 5 || maxRowCount < 5) {
      return { maxRows: -1, maxColumns: -1 };
    }

    return { maxRows: maxRowCount, maxColumns: columnCount };
  }

  getElementUrl(
    elementId,
    elementType,
    elementName,
    worksheetId,
    driveItemInfo,
    additionalData,
  ) {
    let tempParameters = {
      driveItemInfo: driveItemInfo
        ? driveItemInfo
        : this.rootStore.workbooks.activeDriveItemInfo,
      worksheetId: worksheetId,
      elementId: elementId,
      elementType: elementType,
      elementName: elementName,
    };
    if (additionalData) tempParameters.additionalData = additionalData;
    let entityType = elementType === "workbook" ? "workbook" : "element";
    const url =
      "/" +
      entityType +
      "/" +
      getUrlParameterForElementJson(
        tempParameters,
        this.rootStore.encryptionVersion,
      );
    return url;
  }

  getExternalElementUrl(elementId, elementType, elementName, worksheetId) {
    const tempParameters = {
      driveItemInfo: this.rootStore.workbooks.activeDriveItemInfo,
      worksheetId: worksheetId,
      elementId: elementId,
      elementType: elementType,
      elementName: elementName,
    };
    const url =
      "https://app.airrange.io/#/element/" +
      getUrlParameterForElementJson(
        tempParameters,
        this.rootStore.encryptionVersion,
      );
    return url;
  }

  // reloadPage() {
  //   window.location.reload(false);
  // }

  openElement(elementId, elementType, elementName, worksheetId, options) {
    let graphElementId = getGraphElementId(elementId, elementType);

    let elementUrl = this.getElementUrl(
      graphElementId,
      elementType,
      elementName,
      worksheetId,
      null, //driveItemInfo,
      options,
    );

    history.push(elementUrl);
    if (options?.reload) this.rootStore.reloadPage();
  }

  openElementFromExternal(
    driveItemInfo,
    elementId,
    elementType,
    elementName,
    worksheetId,
    options,
  ) {
    let graphElementId = getGraphElementId(elementId, elementType);

    let elementUrl = this.getElementUrl(
      graphElementId,
      elementType,
      elementName,
      worksheetId,
      driveItemInfo,
      options,
    );

    history.push(elementUrl);
    if (options?.reload) this.rootStore.reloadPage();
  }

  openElementFromElementItem(item, options) {
    if (!item) return;
    const elementType = item.elementType ?? item.type;
    const elementId = item.elementId ?? item.id;
    const elementName = item.elementName ?? item.name;

    let graphElementId = getGraphElementId(elementId, elementType);

    let elementUrl = this.getElementUrl(
      graphElementId,
      elementType,
      elementName,
      item.worksheetId,
      item.driveItemInfo,
      options,
    );

    history.push(elementUrl);
    if (options?.reload) this.rootStore.reloadPage();
  }

  openElementUrl(targetUrl, targetType) {
    const existingPrefix =
      targetUrl.includes("workbook/") ||
      targetUrl.includes("invitation/") ||
      targetUrl.includes("element/");
    if (existingPrefix) {
      history.push(targetUrl);
    } else {
      history.push("/element/" + targetUrl);
    }
  }

  openLocalFileBrowser(showFileSelector = true) {
    // set startupInfo for local view
    if (showFileSelector) {
      let startupInfo = {
        type: "open-file",
      };
      this.rootStore.elements.setLocalViewStartupInfo(startupInfo, true);
    }
    // open the view
    this.rootStore.openUrl("/files");
  }

  async saveElementValueChanges(
    accessInfo,
    changesList,
    changeComment,
    shareStatus,
    sharingInformation,
    messageCallback,
  ) {
    try {
      const res = await writeElementValueChanges(
        accessInfo,
        sharingInformation,
        changesList,
        shareStatus,
        messageCallback,
      );
      // refresh the document
      this.rootStore.workbooks.setActiveFileNeedsRefresh(true);

      // write the status to aws
      const sharingInfo = sharingInformation?.sharingInfo;
      if (sharingInfo) {
        const changesInfo = "(changed cells: " + changesList.length + ")";
        const comments = changeComment
          ? changeComment + " " + changesInfo
          : changesInfo;
        await this.rootStore.sharing.updateInvitationStatus(
          sharingInfo,
          shareStatus,
          comments,
        );
      }

      // process notification - email action
      // let sendEmailNotification =
      //   isEmailAddress(sharingInformation.ownerEmail) &&
      //   !isSameText(sharingInformation.ownerEmail, this.rootStore.main.userEmail);

      // if (sendEmailNotification) {
      //   let emailInfo = {
      //     recipient: sharingInformation.ownerEmail,
      //     emailTitle: null,
      //     elementComments: changeComment ? changeComment : "",
      //     elementName: sharingInformation.elementName
      //       ? sharingInformation.elementName
      //       : "",
      //     elementUrl: sharingInformation.elementUrl
      //       ? sharingInformation.elementUrl
      //       : "",
      //   };
      //   await sendChangeNotificationMail(emailInfo);
      // }
      return res;
    } catch (error) {
      console.log("ERROR saveElementValueChanges", error);
    }
  }

  // #endregion
  //========================

  //========================
  // #region VIEWS

  isViewValid(viewType, viewInfo) {
    if (!viewType) return false;
    if (["grid", "pivot"].includes(viewType)) return true;

    let isValid = false;

    if (viewType === "kanban") {
      isValid =
        !is.empty(viewInfo?.stageField) && !is.empty(viewInfo?.displayField);
    }

    if (viewType === "card") {
      isValid = !is.empty(viewInfo?.displayField);
    }

    if (viewType === "list") {
      isValid = !is.empty(viewInfo?.displayField);
    }

    if (viewType === "map") {
      isValid =
        !is.empty(viewInfo?.longitudeField) &&
        !is.empty(viewInfo?.latitudeField) &&
        !is.empty(viewInfo?.displayField);
    }

    if (viewType === "calendar") {
      isValid =
        !is.empty(viewInfo?.startField) && !is.empty(viewInfo?.displayField);
    }

    return isValid;
  }

  // #endregion
  //========================
}
